/** What a field is called, and what may be drawn for it — the two non-component exports.
 *
 * **Their own module because of `react-refresh/only-export-components`**, which is not a style
 * rule: a file mixing components with constants loses fast refresh for the whole file, so editing
 * a label would remount the panel and lose whichever field a reviewer had selected. They lived in
 * `SourceText.tsx` for a few hours and the lint warning is what moved them.
 *
 * `drawn` is here rather than beside the panel for a second reason: it is the single gate for both
 * the highlight and the control that offers it, and its unit tests import it directly. A function
 * two screens depend on, tested as a function, is not a component's private helper.
 */

import type { components } from "../api/schema";

type ExtractedFieldSpan = components["schemas"]["ExtractedFieldSpanOut"];

/** A field's name in a person's words.
 *
 * Here rather than on the review screen because **two** screens need it now — the review's field
 * list and this panel's own list of what can and cannot be pointed at — and the copy that drifted
 * would be whichever was edited second. It is presentation only: nothing branches on it, and an
 * unknown field falls back to its own name rather than to a blank.
 */
export const FIELD_LABEL: Record<string, string> = {
  consumption_kwh: "Consumption",
  period_start: "Period start",
  period_end: "Period end",
  meter_reference: "Meter",
  supplier: "Supplier",
  quantity: "Quantity",
  fuel_type: "Fuel",
  refrigerant_type: "Refrigerant",
  charge_added_kg: "Charge added",
  service_date: "Service date",
  volume_mwh: "Volume",
  vintage_year: "Vintage",
  registry: "Registry",
  certificate_reference: "Certificate",
};

export function fieldLabel(name: string): string {
  return FIELD_LABEL[name] ?? name.replace(/_/g, " ");
}

/** A position this panel is allowed to draw, or `null`.
 *
 * Both halves are checked: the **status**, because that is the server's answer, and then every
 * coordinate, because a `verified` row with a missing number would otherwise render a highlight
 * from `undefined` and silently cover the start of a line. The server refuses to produce either
 * shape; this refuses to draw it, and the two refusals are independent on purpose — a guard is not
 * a guard until something calls it, and the only way to be sure is to have both ends hold.
 */
export interface Drawn {
  readonly page: number;
  readonly line: number;
  readonly start: number;
  readonly length: number;
}

export function drawn(span: ExtractedFieldSpan | undefined): Drawn | null {
  if (!span || span.status !== "verified") return null;
  const { page, line, start, length } = span;
  if (
    typeof page !== "number" ||
    typeof line !== "number" ||
    typeof start !== "number" ||
    typeof length !== "number"
  ) {
    return null;
  }
  // A zero-length highlight is not a place. It would render as an empty `<mark>` that a reader
  // cannot see and a test cannot distinguish from a highlight on the right characters.
  if (length <= 0) return null;
  return { page, line, start, length };
}
